import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { ShoppingBag, ArrowRight, CreditCard } from 'lucide-react';
import { formatCurrency, fetchExchangeRates, convertCurrency } from '../utils/currencyUtils';
import api from '../api/api';
import { areStoreNamesEqual, normalizeStoreName } from '../utils/storeUtils';

const FloatingCart = () => {
    const { card_products, price, card_product_count } = useSelector((state) => state.card);
    const { userInfo } = useSelector((state) => state.auth);
    const { seller, product } = useSelector((state) => state.home);
    const navigate = useNavigate();
    const location = useLocation();
    const [isVisible, setIsVisible] = useState(false);
    const [currencyCode, setCurrencyCode] = useState("NGN");
    const [exchangeLoaded, setExchangeLoaded] = useState(false);

    const getStoreFromUrl = () => {
        const path = location.pathname;
        const parts = path.split('/').filter(Boolean);

        if (parts.length === 0) return null;

        // Check for /stores/:storename/:storeid
        if (parts[0] === 'stores' && parts.length >= 2) {
            return parts[1];
        }

        // Check for /:storeName
        // List of reserved root paths
        const reserved = ['register', 'login', 'forgotpassword', 'resetpassword', 'stores', 'products', 'cart', 'wishlist', 'order', 'shipping', 'payment', 'dashboard', 'about-us', 'privacy-policy', 'return-refund-policy', 'terms-and-conditions', 'order-received'];

        if (parts.length === 1 && !reserved.includes(parts[0].toLowerCase())) {
            return parts[0];
        }

        return null;
    };

    const currentStore = getStoreFromUrl();
    const productSlug = location.pathname.includes('/product/details/') ? location.pathname.split('/').pop() : null;
    const storeToPass = location.state?.fromStore || currentStore || (productSlug ? sessionStorage.getItem(`product-store-${productSlug}`) : null);

    const filteredCardProducts = storeToPass
        ? card_products.filter(p => areStoreNamesEqual(p.shopName, storeToPass))
        : card_products;

    const filteredCount = storeToPass
        ? filteredCardProducts.reduce((acc, group) => acc + group.products.reduce((pAcc, p) => pAcc + p.quantity, 0), 0)
        : card_product_count;

    const calculateTotalPrice = () => {
        let total = 0;
        filteredCardProducts.forEach((group) => {
            group.products.forEach((pt) => {
                const { price, discount, currency } = pt.productInfo;
                const finalPrice = discount
                    ? price - Math.floor((price * discount) / 100)
                    : price;
                total += convertCurrency(finalPrice * pt.quantity, currencyCode, currency);
            });
        });
        return total;
    };

    const filteredPrice = calculateTotalPrice();

    // Flatten products to get a simple list for the image stack
    const allProducts = filteredCardProducts.flatMap(shop => shop.products);
    // Get the last 3 added products for the stack visual
    const recentProducts = allProducts.slice(-3);

    // Determine theme for floating cart
    // Fallback to product's seller info if on a product page and seller state is null
    const isActive = (obj) => obj && Object.keys(obj).length > 0;
    const activeSeller = isActive(seller) ? seller : (isActive(product?.sellerInfo) ? product.sellerInfo : null);
    const themeSettings = activeSeller?.themeSettings;
    const useCustomTheme = themeSettings?.useCustomTheme;

    // When using custom theme, use floatingCart settings (defaults to light theme values)
    // When not using custom theme, use the store's theme preference
    const getThemeClass = () => {
        if (useCustomTheme) {
            // Custom theme is on - we'll use inline styles from floatingCart settings
            return 'custom-theme';
        }
        if (activeSeller?.shopInfo?.theme === 'light') {
            return 'light-theme';
        }
        if (storeToPass) {
            const normalizedStore = normalizeStoreName(storeToPass);
            const cachedTheme = localStorage.getItem(`store-theme-${normalizedStore}`);
            return cachedTheme === 'light' ? 'light-theme' : '';
        }
        return '';
    };
    const themeClass = getThemeClass();
    const isLight = themeClass === 'light-theme';
    const isCustom = themeClass === 'custom-theme';

    // Floating cart custom styles
    const floatingCart = themeSettings?.floatingCart || {
        background: '#ffffff',
        text: '#000000',
        buttonBg: '#000000',
        buttonText: '#ffffff',
        border: 'transparent'
    };

    useEffect(() => {
        fetchExchangeRates().then(() => setExchangeLoaded(true));
    }, []);

    useEffect(() => {
        const fetchUserDetails = async () => {
            if (userInfo) {
                try {
                    const { data } = await api.get("/account/get-details");
                    if (data.currency) {
                        setCurrencyCode(data.currency);
                    }
                } catch (error) {
                    console.log(error);
                }
            }
        };
        fetchUserDetails();
    }, [userInfo]);

    useEffect(() => {
        if (filteredCount > 0) {
            setIsVisible(true);
        } else {
            setIsVisible(false);
        }
    }, [filteredCount]);

    // Hide on cart, shipping, and payment pages to avoid clutter/redundancy
    const isCheckoutPage = ['/cart', '/shipping', '/payment'].includes(location.pathname);
    if (isCheckoutPage) {
        return null;
    }

    if (!isVisible) return null;

    return (
        <div className="fixed bottom-4 left-0 w-full z-[9999] px-4 sm:px-2 flex justify-center pointer-events-none">
            <div className="relative w-full max-w-3xl flex flex-col items-end gap-2 pointer-events-none">
                {filteredCount > 0 && !isCheckoutPage && (
                    <div
                        className={`backdrop-blur-md shadow-2xl rounded-2xl p-3 w-full flex items-center justify-between pointer-events-auto transform transition-all duration-500 ease-in-out animate-in slide-in-from-bottom-10 fade-in ${isCustom ? '' : (isLight ? 'bg-white border border-gray-200 text-black' : 'bg-black/60 border border-white/10 text-white')
                            }`}
                        style={isCustom ? {
                            backgroundColor: floatingCart.background,
                            border: `1px solid ${floatingCart.border || 'transparent'}`,
                            color: floatingCart.text
                        } : {}}
                    >

                        {/* Left Side: Image Stack & Count */}
                        <div className="flex items-center gap-4">
                            <div className="flex -space-x-3">
                                {recentProducts.map((item, index) => (
                                    <div
                                        key={index}
                                        className={`w-10 h-10 rounded-full overflow-hidden ${isCustom ? 'border-2' : (isLight ? 'bg-gray-100 border-2 border-white' : 'bg-gray-800')
                                            }`}
                                        style={isCustom ? { borderColor: floatingCart.background, backgroundColor: floatingCart.background } : {}}
                                    >
                                        <img
                                            src={item.productInfo.images[0]}
                                            alt=""
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                ))}
                            </div>
                            <div className="flex flex-col">
                                <span
                                    className={`text-sm font-medium ${isCustom ? '' : (isLight ? 'text-gray-600' : 'text-gray-300')}`}
                                    style={isCustom ? { color: floatingCart.text, opacity: 0.7 } : {}}
                                >
                                    {filteredCount} {filteredCount === 1 ? 'Item' : 'Items'}
                                </span>
                                <span
                                    className={`text-lg font-bold ${isCustom ? '' : (isLight ? 'text-black' : 'text-white')}`}
                                    style={isCustom ? { color: floatingCart.text } : {}}
                                >
                                    {formatCurrency(filteredPrice, currencyCode, currencyCode)}
                                </span>
                            </div>
                        </div>

                        <button
                            onClick={() => {
                                const cartPath = storeToPass ? `/${storeToPass}/cart` : '/cart';
                                navigate(cartPath, { state: { fromStore: storeToPass } });
                            }}
                            className={`px-6 py-2 rounded-xl font-bold transition-colors flex items-center gap-2 ${isCustom ? 'hover:opacity-90' : (isLight ? 'bg-black text-white hover:bg-gray-800' : 'bg-white text-black hover:bg-gray-200')
                                }`}
                            style={isCustom ? {
                                backgroundColor: floatingCart.buttonBg,
                                color: floatingCart.buttonText
                            } : {}}
                        >
                            <ShoppingBag size={18} />
                            View Cart
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FloatingCart;
